import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useMyMasjids } from '@/features/masjid';
import { errorMessageKey } from '@/shared/lib/errors';
import { prayerLabel } from '@/shared/lib/labels';
import { dateRangeIncludesFriday, todayIso } from '@/shared/lib/dates';
import {
  COMPENSATION_TYPES,
  PRAYER_SLOTS,
  type CompensationType,
  type PrayerSlot,
} from '@/shared/model/enums';
import { Async } from '@/shared/ui/Async';
import { Button } from '@/shared/ui/Button';
import { Chip } from '@/shared/ui/Chip';
import { TextArea } from '@/shared/ui/TextArea';
import { TextField } from '@/shared/ui/TextField';

import { useCreateRequest } from '../hooks/use-coverage';

/** masjid_admin creates a coverage request. */
export function CreateRequestRoute(): JSX.Element {
  const { t } = useTranslation();
  const masjidsQuery = useMyMasjids();

  return (
    <main className="page stack">
      <h1>{t('coverage.create')}</h1>
      <Async
        query={masjidsQuery}
        isEmpty={(l) => l.length === 0}
        emptyMessage={t('coverage.emptyMasjidsForRequest')}
      >
        {(masjids) => <RequestForm masjids={masjids.map((m) => ({ id: m.id, name: m.name }))} />}
      </Async>
    </main>
  );
}

function RequestForm({
  masjids,
}: {
  masjids: readonly { id: string; name: string }[];
}): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const create = useCreateRequest();
  const today = todayIso();

  const [masjidId, setMasjidId] = useState<string | null>(
    masjids.length === 1 ? masjids[0]!.id : null,
  );
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [prayers, setPrayers] = useState<Set<PrayerSlot>>(new Set());
  const [notes, setNotes] = useState('');
  const [compensation, setCompensation] = useState<CompensationType>('volunteer');
  const [error, setError] = useState<string | null>(null);

  // Jumu'ah only makes sense when the covered range includes a Friday. If the
  // dates change so it no longer does, silently drop it from the selection.
  const includesFriday = dateRangeIncludesFriday(startDate, endDate);
  useEffect(() => {
    if (!includesFriday && prayers.has('jumuah')) {
      setPrayers((prev) => {
        const next = new Set(prev);
        next.delete('jumuah');
        return next;
      });
    }
  }, [includesFriday, prayers]);

  const availablePrayers = includesFriday
    ? PRAYER_SLOTS
    : PRAYER_SLOTS.filter((p) => p !== 'jumuah');

  function togglePrayer(prayer: PrayerSlot): void {
    setPrayers((prev) => {
      const next = new Set(prev);
      if (!next.delete(prayer)) next.add(prayer);
      return next;
    });
  }

  function handleSubmit(): void {
    setError(null);
    if (!masjidId) {
      setError(t('coverage.errorMasjid'));
      return;
    }
    if (prayers.size === 0) {
      setError(t('coverage.errorPrayers'));
      return;
    }
    const start = startDate <= endDate ? startDate : endDate;
    const end = endDate >= startDate ? endDate : startDate;
    create.mutate(
      {
        masjidId,
        startDate: start,
        endDate: end,
        prayerSlots: [...prayers],
        notes: notes.trim().length === 0 ? null : notes.trim(),
        compensation,
      },
      {
        onSuccess: () => navigate('/requests'),
        onError: (e) => setError(t(errorMessageKey(e))),
      },
    );
  }

  return (
    <div className="stack">
      <p className="field__label">{t('coverage.masjidLabel')}</p>
      {masjids.length === 1 ? (
        <p>{masjids[0]!.name}</p>
      ) : (
        <div className="wrap">
          {masjids.map((m) => (
            <Chip
              key={m.id}
              label={m.name}
              selected={masjidId === m.id}
              onToggle={() => setMasjidId(m.id)}
            />
          ))}
        </div>
      )}

      <div className="row">
        <TextField
          label={t('coverage.startDate')}
          type="date"
          value={startDate}
          min={today}
          ltr
          onChange={(e) => {
            setStartDate(e.target.value);
            if (endDate < e.target.value) setEndDate(e.target.value);
          }}
        />
        <TextField
          label={t('coverage.endDate')}
          type="date"
          value={endDate}
          min={startDate}
          ltr
          onChange={(e) => setEndDate(e.target.value)}
        />
      </div>

      <p className="field__label">{t('coverage.prayersLabel')}</p>
      <div className="wrap">
        {availablePrayers.map((prayer) => (
          <Chip
            key={prayer}
            label={prayerLabel(t, prayer)}
            selected={prayers.has(prayer)}
            onToggle={() => togglePrayer(prayer)}
          />
        ))}
      </div>
      {!includesFriday ? <p className="muted">{t('coverage.jumuahNeedsFriday')}</p> : null}

      <p className="field__label">{t('coverage.compensationLabel')}</p>
      <div className="wrap">
        {COMPENSATION_TYPES.map((c) => (
          <Chip
            key={c}
            label={t(c === 'volunteer' ? 'coverage.compVolunteer' : 'coverage.compPaidOffline')}
            selected={compensation === c}
            onToggle={() => setCompensation(c)}
          />
        ))}
      </div>

      <TextArea
        label={t('coverage.notesLabel')}
        placeholder={t('coverage.notesHint')}
        value={notes}
        maxLength={1000}
        onChange={(e) => setNotes(e.target.value)}
      />

      {error ? (
        <p className="text-danger" role="alert">
          {error}
        </p>
      ) : null}

      <Button block loading={create.isPending} onClick={handleSubmit}>
        {t('coverage.submit')}
      </Button>
    </div>
  );
}
